import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  CallRoute,
  CallRouteType,
  PhoneNumber,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { DepartmentsApi } from '@frontend/api-departments';
import PhoneOverrideApi from '@frontend/api-overrides-v2';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useSettingsMatch, useSettingsNavigate } from '@frontend/settings-routing';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import {
  Button,
  Modal,
  Text,
  TextLink,
  Tray,
  useModalControl,
  useMultiStepModalControl,
} from '@frontend/design-system';
import { useCallRouteGraphsShallowStore } from '../../components/call-routes/call-route-details/call-route-store';
import { CannotCreateOverrideModal } from '../../components/call-routes/cannot-create-override-modal';
import { ChangeLocationModal } from '../../components/call-routes/change-location-modal';
import { CreateCallRouteModal } from '../../components/call-routes/create-call-route-modal';
import { DeleteCallRouteModal } from '../../components/call-routes/delete-call-route-modal/delete-call-route-modal';
import { EditExtensionsModal } from '../../components/call-routes/edit-extensions-modal';
import { EditPhoneNumbersModal } from '../../components/call-routes/edit-phone-numbers-modal';
import { filterCallRoutesOnly } from '../../components/call-routes/helpers/call-routes';
import { logError } from '../../components/call-routes/helpers/utils';
import { NumbersPanelTabs } from '../../components/call-routes/numbers-panel/call-route-tabs';
import { NumbersPanel } from '../../components/call-routes/numbers-panel/numbers-panel';
import { RenameCallRouteModal } from '../../components/call-routes/rename-call-route-modal';
import { RoutesListTable } from '../../components/call-routes/routes-list-table/routes-list-table';
import {
  RoutesTableTab,
  RoutesTableTabType,
  RoutesTabs,
} from '../../components/call-routes/routes-list-table/routes-tabs';
import { ExtendedCallRoute } from '../../components/call-routes/types';
import { OverrideSettingTray } from '../../components/override/override-settings-tray';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { VideoPlayer } from '../../layouts/video-player';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { useTenantLocationIds } from '../../utils/use-tenant-location-ids';

export function CallRoutesIndex() {
  const { settingsTenantLocation, globalAvailableLocationIds = [] } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const filteredLocations = locationFilters.callRoutesIndex;

  const tenantLocationIds = useTenantLocationIds();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { search } = useSettingsMatch<{ Search: { type?: string } }>();

  useEffect(() => {
    const initialTab =
      search?.type && Object.values(RoutesTableTab).includes(search.type)
        ? (search.type as RoutesTableTabType)
        : RoutesTableTab.CALL_ROUTES;

    setSelectedTab(initialTab);
  }, [search]);

  const { t } = useTranslation('phone');

  const { modalProps: numbersTrayModalProps, triggerProps: numbersTrayTriggerProps } = useModalControl();
  const { modalProps: renameCallRouteModalProps, triggerProps: renameCallRouteTriggerProps } = useModalControl();
  const { triggerProps: createCallRoutesTriggerProps, ...createCallRoutesModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );
  const { modalProps: editPhoneNumbersModalProps, triggerProps: editPhoneNumbersTriggerProps } = useModalControl();
  const { modalProps: editExtensionsModalProps, triggerProps: editExtensionsTriggerProps } = useModalControl();
  const {
    modalProps: changeLocationModalProps,
    triggerProps: changeLocationTriggerProps,
    openModal: openChangeLocationModal,
  } = useModalControl();
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();
  const overrideTray = useModalControl();
  const cannotCreateOverrideModal = useModalControl();
  const [pendingOverrideAfterLocationSave, setPendingOverrideAfterLocationSave] = useState(false);

  const { allTempEditingNodes } = useCallRouteGraphsShallowStore('allTempEditingNodes');

  const [selectedTab, setSelectedTab] = useState<RoutesTableTabType>(RoutesTableTab.CALL_ROUTES);

  const unsavedChangesCallRouteIds = useMemo(() => {
    const allCallRouteIds = Object.entries(allTempEditingNodes)
      .map(([callRouteId, unsavedNodes]) => (!!unsavedNodes?.length ? callRouteId : undefined))
      .filter(Boolean);

    return allCallRouteIds;
  }, [allTempEditingNodes]);

  const { data: departments = [], isLoading: departmentsIsLoading } = useQuery({
    queryKey: [queryKeys.listDepartments()],
    queryFn: () => {
      return DepartmentsApi.listDept({});
    },
    select: (data) => data.departments ?? [],
    staleTime: 0,
  });

  // The ID of the route (call route or device route) that has been selected in some way for viewing or editing (e.g.
  // clicked on a "+ more" text link next to phone numbers or extensions).
  const [selectedRouteId, setSelectedRouteId] = useState<string>();
  const [initialSidePanelTab, setInitialSidePanelTab] = useState(NumbersPanelTabs.PHONE_NUMBERS);

  const { data: routes = [], isLoading: callRoutesIsLoading } = CallRouteV1.Queries.useListQuery({
    request: {
      tenantId: settingsTenantLocation?.phoneTenantId ?? '',
      includes: { deviceExtensionRoutes: true },
    },
    options: {
      enabled: !!settingsTenantLocation?.phoneTenantId,
      select: ({ callRoutes, deviceExtensionRoutes }) => {
        // Sort the extension numbers so that they are displayed in ascending order.
        const sortedCallRoutes = (callRoutes ?? []).reduce<CallRoute[]>((acc, callroute) => {
          return [
            ...acc,
            {
              ...callroute,
              extensionNumbers: callroute.extensionNumbers.sort(),
            },
          ];
        }, []);

        const sortedDeviceRoutes = (deviceExtensionRoutes ?? []).reduce<CallRoute[]>((acc, deviceRoute) => {
          return [
            ...acc,
            {
              ...deviceRoute,
              extensionNumbers: deviceRoute.extensionNumbers.sort(),
            },
          ];
        }, []);

        return [...sortedCallRoutes, ...sortedDeviceRoutes];
      },
      cacheTime: 1000,
      staleTime: 1000,
    },
  });

  // Filter the routes based on the locations selected from the global location selector.
  const filteredRoutes = useMemo(
    () =>
      scopedFilterAndSort({
        data: routes,
        scopeIdAccessor: (route) => (route.locationId ? [route.locationId] : []),
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
      }),
    [routes, globalAvailableLocationIds, filteredLocations]
  );

  const {
    data: overridesData = { phoneOverrides: [], lastUsed: {}, deviceExtensionOverrides: [] },
    isLoading: phoneOverridesIsLoading,
  } = useQuery({
    queryKey: [
      settingsTenantLocation?.phoneTenantId,
      ...queryKeys.settings.listOverrides({
        includeDeviceExtensionOverrides: true,
      }),
    ],
    queryFn: async () =>
      PhoneOverrideApi.List({
        tenantId: settingsTenantLocation?.phoneTenantId ?? '',
        includes: { deviceExtensionOverrides: true },
      }),
    enabled: !!settingsTenantLocation?.phoneTenantId,
    retry: false,
  });

  const phoneOverrides = useMemo(
    () => [...overridesData.phoneOverrides, ...(overridesData.deviceExtensionOverrides ?? [])],
    [overridesData.phoneOverrides, overridesData.deviceExtensionOverrides]
  );
  const selectedPhoneOverride = phoneOverrides.find((override) => override.callRouteId === selectedRouteId);
  const validPhoneOverrides = phoneOverrides.filter((override) => override.id);

  const { data: allPhoneNumbers, isLoading: phoneNumbersIsLoading } =
    PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
      request: { locationIds: tenantLocationIds, voice: true, excludeTenDlc: true },
      options: { enabled: !!tenantLocationIds.length, select: (data) => data?.phoneNumbers ?? [], retry: false },
    });

  const { data: unallocatedPhoneNumbers = [], isLoading: unallocatedPhoneNumbersIsLoading } =
    CallRouteV1.Queries.useListUnallocatedPhoneNumbersQuery({
      request: { tenantId: settingsTenantLocation?.phoneTenantId ?? '' },
      options: {
        select: (data) => data?.unallocatedPhoneNumbers ?? [],
        retry: false,
        enabled: !!settingsTenantLocation?.phoneTenantId,
        cacheTime: 1000,
        staleTime: 1000,
      },
    });

  const isLoading =
    callRoutesIsLoading ||
    phoneOverridesIsLoading ||
    phoneNumbersIsLoading ||
    unallocatedPhoneNumbersIsLoading ||
    departmentsIsLoading;

  const isUnifyLocation = settingsTenantLocation?.locationType === 'unify';

  // tableData is the routes data (both: callRoutes and deviceExtensionRoutes) with the phoneNumbers mapped to the
  // routes (since the routes only have an array of phoneNumberIds) and also the phoneOverrides. The phoneOverrides
  // data has the callRouteId that maps to a callRoute.
  const tableData = useMemo(
    () =>
      filteredRoutes?.map((callRoute) => {
        const phoneNumberIds = callRoute.phoneNumberIds ?? [];
        const phoneNumbersForCallRoute =
          allPhoneNumbers?.filter((phoneNumber) => phoneNumberIds.includes(phoneNumber.id)) ?? [];
        const phoneNumbers: PhoneNumber[] = phoneNumbersForCallRoute.map((phoneNumber) => ({
          id: phoneNumber.id,
          phoneNumber: String(phoneNumber.phoneNumber?.nationalNumber ?? ''),
          locationId: phoneNumber.locationId ?? '',
          displayName: phoneNumber.displayName ?? '',
        }));

        const callRoutePhoneOverride = validPhoneOverrides?.find(
          (phoneOverride) => phoneOverride.instructionSetId === callRoute.rootInstructionSetId
        );

        return {
          ...callRoute,
          phoneNumbers,
          callRoutePhoneOverride,
        };
      }) ?? [],
    [filteredRoutes, allPhoneNumbers, phoneOverrides]
  );

  // The call route or device route that has been selected in some way for viewing or editing
  // (e.g. clicked on a "+ more" text link next to phone numbers or extensions).
  const selectedRoute = useMemo(
    () => tableData?.find((route) => route.callRouteId === selectedRouteId),
    [tableData, selectedRouteId]
  );

  const moreTextClickHandler = (type: NumbersPanelTabs, callRoute?: ExtendedCallRoute) => {
    if (!callRoute) {
      logError('Call Route undefined in moreTextClickHandler(): PC:8d73jl4', 'CallRoutesIndex.moreTextClickHandler', {
        type,
      });
      alert(t('Call Route not found'));
      return;
    }

    setSelectedRouteId(callRoute.callRouteId);
    setInitialSidePanelTab(type);
    numbersTrayTriggerProps.onClick();
  };

  const handleTriggerModal = (callRouteId: string, modalTriggerFn: () => void) => {
    setSelectedRouteId(tableData.find((route) => route.callRouteId === callRouteId)?.callRouteId);
    // Close the side modal if it is open
    numbersTrayModalProps.onClose();
    modalTriggerFn();
  };

  const handleOpenCreateCallRoute = () => {
    createCallRoutesModalProps.goToStep({ orIndex: 0 });
    createCallRoutesTriggerProps.onClick();
  };

  const hasOverridesManagementPermission = useOverridesManagementPermission();

  return (
    <>
      <PhoneSettingsPage
        renderContainer={false}
        title={t('Call Routes')}
        subtitle={
          <Trans t={t}>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(1)};
              `}
            >
              <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                <Text>
                  Define the experience for your callers and how they get to your staff. Create and manage{' '}
                  <strong>Call Routes</strong> to define the call experience for your main business phone numbers.
                  Manage <strong>Device Routes</strong> to define the call experience when your devices are called by
                  extension or optionally by phone number.
                </Text>
                <Text>
                  Learn more on{' '}
                  <TextLink
                    rel='noopener noreferrer'
                    target='_blank'
                    href='https://www.weavehelp.com/weavehelp/article/call-routing-guide'
                    trackingId={trackingId({
                      context: 'setting',
                      feature: 'call-routes',
                      details: 'weave-help-link',
                    })}
                  >
                    Weave Help
                  </TextLink>
                  .
                </Text>
              </div>
            </div>
          </Trans>
        }
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Call Routes'), to: '/phone/call-routes' },
        ]}
      >
        {settingsTenantLocation ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(4)};
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <RoutesTabs
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                trackingIdPrefix='call-routes-list-page-'
              />
            </div>
            <div
              css={css`
                background-color: ${theme.colors.white};
                border-radius: ${theme.borderRadius.medium};
                padding: ${theme.spacing(3)};
                box-shadow: ${theme.shadows.light};
              `}
            >
              <RoutesListTable
                tableData={tableData}
                filteredLocations={filteredLocations}
                setLocationFilters={setLocationFilters}
                isLoading={isLoading}
                settingsTenantLocation={settingsTenantLocation}
                unsavedChangesCallRouteIds={unsavedChangesCallRouteIds}
                clickableCallRoutes={true}
                initialSelectedCallRouteId={selectedRouteId}
                onMoreTextClick={moreTextClickHandler}
                selectedTab={selectedTab}
                rightToolbarContent={
                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      gap: ${theme.spacing(1)};
                      align-items: center;
                    `}
                  >
                    {selectedTab === RoutesTableTab.CALL_ROUTES ? (
                      <>
                        <VideoPlayer
                          wistiaVideoId='juxckj7x7v'
                          trackingId={trackingId({
                            context: 'setting',
                            feature: 'call-routes',
                            details: 'play-help-video-btn',
                          })}
                        />
                        <Button
                          css={css`
                            background-color: ${theme.colors.white};
                          `}
                          variant='secondary'
                          iconName='plus'
                          onClick={handleOpenCreateCallRoute}
                          disabled={isLoading}
                          trackingId={trackingId({
                            context: 'setting',
                            feature: 'call-routes',
                            details: 'create-call-route-btn',
                          })}
                        >
                          {t('Create Call Route')}
                        </Button>
                      </>
                    ) : (
                      <VideoPlayer
                        wistiaVideoId='ivr709qb1h'
                        trackingId={trackingId({
                          context: 'setting',
                          feature: 'device-extension-routes',
                          details: 'play-help-video-btn',
                        })}
                      />
                    )}
                  </div>
                }
                tableRowActions={{
                  callRouteRowActions: {
                    actions: [
                      {
                        label: t('Edit Call Route'),
                        Icon: () => null,
                        onClick: (row) => {
                          settingsNavigate({ to: '/phone/call-routes/:id', params: { id: row.callRouteId } });
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'edit-call-route-menu-btn',
                        }),
                      },
                      {
                        label: t('Rename Call Route'),
                        Icon: () => null,
                        onClick: (row) => {
                          handleTriggerModal(row.callRouteId, renameCallRouteTriggerProps.onClick);
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'rename-call-route-menu-btn',
                        }),
                      },
                      {
                        label: t('Assign Phone Numbers'),
                        Icon: () => null,
                        onClick: (row) => {
                          handleTriggerModal(row.callRouteId, editPhoneNumbersTriggerProps.onClick);
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'edit-phone-numbers-menu-btn',
                        }),
                      },
                      {
                        label: t('Assign Route Extensions'),
                        Icon: () => null,
                        onClick: (row) => {
                          handleTriggerModal(row.callRouteId, editExtensionsTriggerProps.onClick);
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'edit-extensions-menu-btn',
                        }),
                      },
                      {
                        label: t('Change Location'),
                        Icon: () => null,
                        onClick: (row) => {
                          handleTriggerModal(row.callRouteId, changeLocationTriggerProps.onClick);
                        },
                        hide: !isUnifyLocation,
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'change-location-menu-btn',
                        }),
                      },
                      {
                        label: (row) => (row.callRoutePhoneOverride ? t('Edit Override') : t('Create Override')),
                        hide: (row) => !hasOverridesManagementPermission(row?.locationId ?? ''),
                        Icon: () => null,
                        onClick: (row) => {
                          setSelectedRouteId(row.callRouteId);
                          if (!row.locationId) {
                            // Overrides require a location to be set on the Call Route.
                            cannotCreateOverrideModal.openModal();
                          } else {
                            overrideTray.openModal();
                          }
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'create-override-btn',
                        }),
                      },
                      {
                        label: t('Delete Call Route'),
                        Icon: () => null,
                        onClick: (row) => {
                          handleTriggerModal(row.callRouteId, deleteTriggerProps.onClick);
                        },
                        destructive: true,
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'call-routes',
                          details: 'delete-menu-btn',
                        }),
                      },
                    ],
                  },
                  deviceRouteRowActions: {
                    actions: [
                      {
                        label: t('Edit Device Route'),
                        Icon: () => null,
                        onClick: (row) => {
                          settingsNavigate({ to: '/phone/call-routes/:id', params: { id: row.callRouteId } });
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'device-extension-route',
                          details: 'edit-device-route-menu-btn',
                        }),
                      },
                      {
                        label: t('Assign Phone Numbers'),
                        Icon: () => null,
                        onClick: (row) => {
                          handleTriggerModal(row.callRouteId, editPhoneNumbersTriggerProps.onClick);
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'device-extension-route',
                          details: 'device-route-edit-phone-numbers-menu-btn',
                        }),
                      },
                      {
                        label: (row) => (row.callRoutePhoneOverride ? t('Edit Override') : t('Create Override')),
                        Icon: () => null,
                        onClick: (row) => {
                          setSelectedRouteId(row.callRouteId);
                          if (!row.locationId) {
                            // Overrides require a location to be set on the Device Extension Route.
                            cannotCreateOverrideModal.openModal();
                          } else {
                            overrideTray.openModal();
                          }
                        },
                        trackingId: trackingId({
                          context: 'setting',
                          feature: 'device-extension-route',
                          details: 'device-route-edit-override-menu-btn',
                        }),
                      },
                    ],
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <div>{t('No Phone Tenant Selected')}</div>
        )}
      </PhoneSettingsPage>

      {selectedRoute && (
        <Tray
          mountTarget='[data-settings-modal-content]'
          width='large'
          css={{ padding: theme.spacing(3) }}
          {...numbersTrayModalProps}
        >
          <SurfaceHeader.Root
            noBorder
            css={{ margin: theme.spacing(-3, -3, -2, -3), width: `calc(100% + ${theme.spacing(6)})` }}
          >
            <SurfaceHeader.Row>
              <SurfaceHeader.Title>{t('Numbers')}</SurfaceHeader.Title>
              <SurfaceHeader.Actions>
                <SurfaceHeader.Icon
                  iconName='x'
                  onClick={() => {
                    setSelectedRouteId(undefined);
                    numbersTrayModalProps.onClose();
                  }}
                />
              </SurfaceHeader.Actions>
            </SurfaceHeader.Row>
            <SurfaceHeader.Body>{selectedRoute.name}</SurfaceHeader.Body>
          </SurfaceHeader.Root>
          <NumbersPanel
            callRoute={selectedRoute}
            initialTab={initialSidePanelTab}
            hasChildLocations={isUnifyLocation}
            isDeviceExtensionRoute={selectedRoute.callRouteType === CallRouteType.CALL_ROUTE_TYPE_EXTENSION}
            onEditPhoneNumbers={(callRouteId) => {
              handleTriggerModal(callRouteId, editPhoneNumbersTriggerProps.onClick);
            }}
            onEditExtensions={(callRouteId) => {
              handleTriggerModal(callRouteId, editExtensionsTriggerProps.onClick);
            }}
          />
        </Tray>
      )}

      {settingsTenantLocation && (
        <CreateCallRouteModal
          {...createCallRoutesModalProps}
          tenantLocation={settingsTenantLocation}
          unallocatedPhoneNumbers={unallocatedPhoneNumbers}
          onSuccess={(data) =>
            settingsNavigate({
              to: '/phone/call-routes/:id',
              params: { id: data.callRouteId },
            })
          }
        />
      )}

      {selectedRoute && settingsTenantLocation && (
        <Modal {...editPhoneNumbersModalProps} minWidth={600}>
          <EditPhoneNumbersModal
            onClose={() => {
              setSelectedRouteId(undefined);
              editPhoneNumbersModalProps.onClose();
            }}
            callRoute={selectedRoute}
            tenantLocation={settingsTenantLocation}
            unallocatedPhoneNumbers={unallocatedPhoneNumbers ?? []}
            isDeviceExtensionRoute={selectedRoute.callRouteType === CallRouteType.CALL_ROUTE_TYPE_EXTENSION}
          />
        </Modal>
      )}

      {selectedRoute && settingsTenantLocation && (
        <Modal {...editExtensionsModalProps} minWidth={600}>
          <EditExtensionsModal
            onClose={() => {
              setSelectedRouteId(undefined);
              editExtensionsModalProps.onClose();
            }}
            callRoute={selectedRoute}
            tenantLocation={settingsTenantLocation}
          />
        </Modal>
      )}

      {selectedRoute && (
        <Modal {...renameCallRouteModalProps} minWidth={400}>
          <RenameCallRouteModal
            onClose={() => {
              setSelectedRouteId(undefined);
              renameCallRouteModalProps.onClose();
            }}
            callRoute={selectedRoute}
          />
        </Modal>
      )}

      {selectedRoute && settingsTenantLocation && (
        <Modal
          {...changeLocationModalProps}
          onClose={() => {
            setPendingOverrideAfterLocationSave(false);
            setSelectedRouteId(undefined);
            changeLocationModalProps.onClose();
          }}
          minWidth={580}
        >
          <ChangeLocationModal
            onClose={() => {
              setPendingOverrideAfterLocationSave(false);
              setSelectedRouteId(undefined);
              changeLocationModalProps.onClose();
            }}
            callRoute={selectedRoute}
            settingsTenantLocation={settingsTenantLocation}
            onSuccess={
              pendingOverrideAfterLocationSave
                ? () => {
                    changeLocationModalProps.onClose();
                    setPendingOverrideAfterLocationSave(false);
                    overrideTray.openModal();
                  }
                : undefined
            }
          />
        </Modal>
      )}

      {selectedRoute && (
        <Modal {...cannotCreateOverrideModal.modalProps} minWidth={580}>
          <CannotCreateOverrideModal
            callRouteName={selectedRoute.name}
            onClose={cannotCreateOverrideModal.modalProps.onClose}
            onAssignLocationClick={() => {
              cannotCreateOverrideModal.closeModal();
              setPendingOverrideAfterLocationSave(true);
              openChangeLocationModal();
            }}
          />
        </Modal>
      )}

      {deleteModalProps.show && (
        <DeleteCallRouteModal
          deleteModalProps={deleteModalProps}
          callRouteId={selectedRouteId}
          callRouteName={selectedRoute?.name ?? ''}
          callRoutes={filterCallRoutesOnly(routes)}
          phoneNumbers={tableData.find((callRoute) => callRoute.callRouteId === selectedRouteId)?.phoneNumbers ?? []}
          department={departments.find((dept) => dept.callRouteId === selectedRoute?.callRouteId)}
          onSuccess={() => {
            setSelectedRouteId(undefined);
          }}
        />
      )}

      <OverrideSettingTray
        modalControls={overrideTray}
        mountTarget='[data-settings-modal-content]'
        override={selectedPhoneOverride}
      />
    </>
  );
}
